import { useEffect, useRef, useState } from 'react';
import { chatIdentity, getMessages, sendMessage } from '../lib/api';
import type { ChatMessage, Group } from '../types';
import { Icon } from './Icon';

export function ChatRoom({ group, onClose }: { group: Group | null; onClose: () => void }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const identity = chatIdentity();

  useEffect(() => {
    if (!group) return;
    let active = true;
    const load = async () => {
      try { const next = await getMessages(group.id); if (active) { setMessages(next); setError(''); } }
      catch { if (active) setError('채팅 연결을 확인하고 있어요.'); }
    };
    setMessages([]); setDraft(''); void load();
    const timer = window.setInterval(() => void load(), 3000);
    return () => { active = false; window.clearInterval(timer); };
  }, [group]);

  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' }); }, [messages]);
  if (!group) return null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); const body = draft.trim(); if (!body || sending) return;
    setSending(true);
    try { const message = await sendMessage(group.id, body); setMessages((items) => [...items, message]); setDraft(''); setError(''); }
    catch { setError('메시지를 보내지 못했어요. 다시 시도해주세요.'); }
    finally { setSending(false); }
  };

  return <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
    <section className="sheet chat-sheet" role="dialog" aria-modal="true" aria-label={`${group.title} 채팅`} onMouseDown={(event) => event.stopPropagation()}>
      <div className="sheet-handle"/>
      <div className="chat-header"><div><span className="eyebrow">모임 채팅</span><h2>{group.title}</h2><small>{identity.nickname}으로 참여 중</small></div><button className="icon-button" onClick={onClose} aria-label="닫기"><Icon name="close"/></button></div>
      <div className="chat-messages" ref={listRef}>
        {!messages.length && !error && <div className="chat-empty"><span>💬</span><b>첫 메시지를 남겨보세요</b><small>모임 장소와 준비물을 함께 이야기해요.</small></div>}
        {messages.map((message) => { const mine = message.memberId === identity.memberId; return <div className={`chat-line ${mine ? 'mine' : ''}`} key={message.id}>{!mine && <small>{message.nickname}</small>}<p>{message.body}</p><time>{new Date(message.createdAt).toLocaleTimeString('ko-KR',{hour:'2-digit',minute:'2-digit'})}</time></div>; })}
      </div>
      {error && <p className="chat-error">{error}</p>}
      <form className="chat-compose" onSubmit={submit}><input value={draft} onChange={(event) => setDraft(event.target.value)} maxLength={300} placeholder="메시지를 입력하세요"/><button disabled={!draft.trim() || sending} aria-label="메시지 보내기">➤</button></form>
    </section>
  </div>;
}
